import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { path } from 'ramda';
import { useNavigate } from 'react-router-dom';
import { Button, Divider, Flex, Form, Input } from '@app/ui';
import {
    ArrowLeftOutlined,
    LoginOutlined,
    SearchOutlined,
} from '@ant-design/icons';

import { User } from '@app/types/api';

import connect from '@app/utils/connect';
import userActions from '@app/actions/user';
import substringMatch from '@app/utils/substringMatch';
import { ClientSelectionListItem } from '../ClientSelectionListItem';

export type ClientSelectionFormProps = {
    backToLogin: () => void;
    selectClient: (clientId: string) => void;
    user: User;
};

export const ClientSelectionForm = ({
    backToLogin,
    user,
    selectClient,
}: ClientSelectionFormProps) => {
    const { t } = useTranslation('login');
    const [clientId, setClientId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const { isLoggedIn, clients = [] } = user;

    const proceedWithLogIn = () => {
        selectClient(clientId);
    };

    const _onChangeSearchTerm = e => setSearchTerm(e.target.value);

    const renderedClientList = useMemo(() => {
        const sortedClients = clients.sort((a, b) =>
            a.name.localeCompare(b.name),
        );

        return sortedClients.map(client => {
            const { id, name } = client;

            const _onClick = () => setClientId(id);

            const isSelected = clientId === id;

            const itMatches = substringMatch(searchTerm, name);

            if (!itMatches) return;

            return (
                <ClientSelectionListItem
                    key={client.id}
                    isSelected={isSelected}
                    client={client}
                    _onClick={_onClick}
                />
            );
        });
    }, [clients, clientId, searchTerm]);

    useEffect(() => {
        if (isLoggedIn) {
            const client = clients.find(c => c.id === clientId);

            const valuationStatusId = client ? client.valuationStatusId : null;

            const showOnboardingOnly = valuationStatusId == '1';

            const from = showOnboardingOnly
                ? `/c/${clientId}/client-configuration`
                : path(['state', 'from', 'pathname'], location) || '/';

            navigate(from);
        }
    }, [isLoggedIn]);

    return (
        <Flex vertical className="-form">
            <Form name="clientSelection" layout="vertical" autoComplete="off">
                <Form.Item
                    label={t('clientSelection.chooseAccount')}
                    name="client"
                    className="-m-b-2"
                >
                    <Input
                        placeholder={t('clientSelection.searchAccount')}
                        type="text"
                        value={searchTerm}
                        suffix={<SearchOutlined />}
                        onChange={_onChangeSearchTerm}
                    />
                </Form.Item>

                <div
                    style={{ maxHeight: 200, overflow: 'auto' }}
                    className="-client-selection-list -m-v-2"
                >
                    {renderedClientList}
                </div>

                <Form.Item>
                    <Button
                        type="primary"
                        icon={<LoginOutlined />}
                        iconPosition="end"
                        onClick={proceedWithLogIn}
                        disabled={!clientId}
                    >
                        {t('clientSelection.logIn')}
                    </Button>
                </Form.Item>

                <Divider />

                <Button
                    onClick={backToLogin}
                    type="link"
                    icon={
                        <ArrowLeftOutlined
                            style={{ color: 'rgba(0, 159, 223, 1)' }}
                        />
                    }
                    iconPosition="start"
                >
                    {t('clientSelection.backToSignIn')}
                </Button>
            </Form>
        </Flex>
    );
};

export default connect(['user'], userActions)(ClientSelectionForm);
